import { CircleStop } from 'lucide-react';
import { useState } from 'react';
import type { Job, Machine } from '@orgus/protocol';
import { Badge, Button, ConfirmDialog, Dialog, LogViewer, RelativeTime, formatDuration, jobLabel, jobTone, shortPath, toast, useNow } from '@orgus/ui';
import { runAction } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { useLogStream } from '../../lib/useLogStream.ts';

/**
 * One command's output, live while it runs, with Cancel for a running command. The output
 * comes from the machine's ring buffer (the last 2 MB), so it is there after the fact too.
 */
export function JobLogDialog({ job, machine, onClose }: { job: Job | undefined; machine: Machine | undefined; onClose: () => void }) {
  const open = !!job;
  const mode = useOrgus((s) => s.mode);
  const stream = useLogStream(job ? { type: 'job', id: job.id } : null, open);
  const [confirming, setConfirming] = useState(false);
  const now = useNow(1000);
  const running = job?.state === 'running';

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(v) => !v && onClose()}
        size="xl"
        title={job ? <span className="font-mono text-md">{job.command}</span> : 'Command'}
        description={
          job ? (
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <Badge tone={jobTone(job)} dot>
                {jobLabel(job)}
                {job.state === 'failed' && job.exitCode != null ? ` (exit ${job.exitCode})` : ''}
              </Badge>
              <span>{shortPath(job.cwd, 60)}</span>
              {machine && (
                <>
                  <span aria-hidden>·</span>
                  <span>{machine.name}</span>
                </>
              )}
              <span aria-hidden>·</span>
              <RelativeTime ts={job.startedAt} prefix="started" />
              <span aria-hidden>·</span>
              <span className="num">{formatDuration((job.endedAt ?? now) - job.startedAt)}</span>
            </span>
          ) : undefined
        }
        bodyClassName="flex flex-col gap-3 pb-5"
        footer={
          running ? (
            <Button variant="ghost" icon={<CircleStop />} className="text-failed hover:bg-failed-soft hover:text-failed" onClick={() => setConfirming(true)}>
              Cancel command
            </Button>
          ) : undefined
        }
      >
        <LogViewer
          label={`Output of ${job?.command ?? 'the command'}`}
          text={mode === 'mock' ? `$ ${job?.command ?? ''}\nDemo data has no command output. Connect to a hub to see it live.\n` : (stream.log?.text ?? '')}
          live={mode === 'live' && running && !stream.log?.ended}
          loading={mode === 'live' && stream.loading}
          error={stream.error}
          onRetry={stream.retry}
          emptyText={running ? 'Running. Output appears as the command prints it.' : 'The command printed nothing.'}
          className="h-[56vh]"
        />
      </Dialog>
      <ConfirmDialog
        open={confirming && !!job && running}
        onOpenChange={setConfirming}
        title={`Cancel ${job?.command ?? 'this command'}?`}
        description={`Stops every process it started on ${machine?.name ?? 'its machine'}; changes it already made stay.`}
        confirmLabel="Cancel command"
        onConfirm={async () => {
          if (!job) return;
          await runAction(job.machineId, 'job.cancel', { jobId: job.id });
          toast.success('Command cancelled', job.command);
        }}
      />
    </>
  );
}
